import { Analytics } from '@cobliteam/analytics/src/Analytics';
import { IdentificationTokenType } from '@cobliteam/api-lib';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useEffect, useState } from 'react';

interface MultiSelectIdentificationTokenTypeProps {
  size?: 'sm' | 'md';
  selectedTypes: IdentificationTokenType[];
  isOpen?: boolean;
  titleComponent?: React.ReactNode;
  onClick?: () => void;
  onChange: (types: IdentificationTokenType[]) => void;
}

interface IdentificationTokenTypeSelectOption {
  type: IdentificationTokenType;
  label: string;
}

const MultiSelectIdentificationTokenType = ({
  size = 'md',
  selectedTypes,
  isOpen,
  titleComponent,
  onClick: onDropdownClick,
  onChange,
}: MultiSelectIdentificationTokenTypeProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState<IdentificationTokenType[]>(selectedTypes || []);

  const hasSameElements =
    selectedTypes?.length === draft?.length &&
    Array.from(selectedTypes).every((value) => {
      return draft.find((item) => item === value);
    });

  const onClick = () => {
    onDropdownClick?.();
    setDraft(selectedTypes || []);
  };

  const handleClose = () => {
    setForceClosed(true);
    setDraft(selectedTypes || []);
  };

  const handleApply = () => {
    Analytics.track('Filtered Identification Token Type', {
      selectedTypes: draft,
    });

    onChange(draft);
    handleClose();
  };

  const toggleItem = (dataItem: IdentificationTokenType) => {
    const isSelected = draft.find((d) => d === dataItem);
    const updatedSelected = isSelected ? draft.filter((d) => d !== dataItem) : [...draft, dataItem];
    setDraft(updatedSelected);
  };

  useEffect(() => {
    setForceClosed(!isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const data: IdentificationTokenTypeSelectOption[] = [
    {
      type: IdentificationTokenType.IBUTTON,
      label: t('identification_token_type.keychain'),
    },
    {
      type: IdentificationTokenType.RFID,
      label: t('identification_token_type.rfid'),
    },
  ];

  return (
    <Dropdown
      onOpenChange={onClick}
      forceClose={forceClosed}
      portal={false}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={() => setDraft([])}>
              {t('identification_token_type.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightList={300}
              heightItem={28}
              data={data}
              itemComponent={(item: IdentificationTokenTypeSelectOption) => (
                <div className='flex items-center gap-2'>
                  <Checkbox
                    key={`identification-token-type-${item}`}
                    id={`identification-token-type-${item}`}
                    checked={draft.some((d) => d === item.type)}
                    onCheckedChange={() => toggleItem(item.type)}
                    data-testid={`checkbox-identification-token-type-${item.label}`}
                  />
                  <Label htmlFor={`identification-token-type-${item.type}`}>{item.label}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button variant='ghost' full size='sm' onClick={handleClose}>
                {t('identification_token_type.cancel')}
              </Button>
              <Button size='sm' full onClick={handleApply} disabled={hasSameElements}>
                {t('identification_token_type.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <div>
        {titleComponent || (
          <Button size={size} variant='ghost' type='button' startIcon={<Icon name='grid-2x2' />}>
            {t('identification_token_type.title')} {selectedTypes?.length > 0 ? `(${selectedTypes?.length})` : ''}
          </Button>
        )}
      </div>
    </Dropdown>
  );
};

export default MultiSelectIdentificationTokenType;
